// Products › Lens options (UC-CATALOGUE-009; no prototype screen — designed from the use case in the Products vocabulary): a
// lens lab's coatings, tints and PPF, grouped by kind, each with its code, description, tariff code, ex-VAT cost, VAT-inclusive
// selling price, the lens it is restricted to and whether it is active. Every role reads them; the stock roles add, edit,
// deactivate and reactivate them (never delete) under the option's version. Only lens labs are offered (BR-CATALOGUE-017).
// The form's Suggest asks pricing for the price the lab's lens rule gives for the cost at the acting branch (UC-PRICING-007 A9):
// the lens markup of the lab's list in force there and the branch's lens rounding; with no list in force it says so and the
// price is typed (UC-CATALOGUE-009 A7).
import { CATALOGUE_LIMITS, LENS_COMPONENT_OPTION_KINDS, LENS_COMPONENT_OPTION_KIND_LABELS, type LensComponentOptionKind, type LensComponentOptionView, type ProductSummary, type SaoaCodeView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { lensOptions, tariffCodes, type OptionBody } from "../api/endpoints.catalogue";
import { pricing } from "../api/endpoints.pricing";
import { suppliers } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { formatMoney, percentFromBp } from "../lib/format";
import { centsFromText, textFromCents } from "../lib/money-input";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, Loading, Modal, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SuggestInput } from "../ui/typeahead";
import { ProductPicker, activeBranchName, refusalWords } from "./CatalogueShared";
import { NoAccessPanel, PageHead } from "./common";

const etagOf = (o: { version: number }): string => `W/"${o.version}"`;
/** An option's kind inside a sentence: "coating", "tint" — but PPF stays an abbreviation ("No PPF options."). */
const kindWord = (k: LensComponentOptionKind): string => { const label = LENS_COMPONENT_OPTION_KIND_LABELS[k]; return label === label.toUpperCase() ? label : label.toLowerCase(); };

export function LensOptions() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const title = block ? WRITE_BLOCK_TITLE[block] : undefined;
  const canWrite = can(s, "catalogue.product.write");
  const [sp, setSp] = useSearchParams();
  const supplierId = sp.get("supplier") ?? "";
  const showInactive = sp.get("inactive") === "true";
  const labs = useQuery({ queryKey: ["suppliers", "lens-labs"], queryFn: () => suppliers.list({ status: "Active", type: "LensLab" }, { limit: 200 }), staleTime: 60_000 });
  const list = useQuery({ queryKey: ["lens-options", supplierId, showInactive], queryFn: () => lensOptions.list({ supplierId, includeInactive: showInactive }), enabled: supplierId !== "" });
  const [editing, setEditing] = useState<LensComponentOptionView | "new" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // A toggle's refusal, with the option it was about; a conflict reads the list again so the next toggle is under the new version.
  const [error, setError] = useState<{ err: unknown; label: string } | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const lab = labs.data?.data.items.find((l) => l.id === supplierId) ?? null;
  const rows = list.data?.data.items ?? [];
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["lens-options"] }); };
  const toggle = async (o: LensComponentOptionView) => {
    if (toggling !== null) return;
    setError(null);
    setToggling(o.id);
    try {
      await lensOptions.patch(o.id, { isActive: !o.isActive }, etagOf(o));
      setNotice(`${o.supplierCode} ${o.description} ${o.isActive ? "deactivated — offered nowhere, kept on whatever used it" : "reactivated"}.`);
      refresh();
    } catch (err) {
      setNotice(null);
      setError({ err, label: `${o.supplierCode} ${o.description}` });
      if (isApiError(err) && err.code === "VERSION_CONFLICT") refresh();
    } finally { setToggling(null); }
  };
  const denied = labs.isError && isApiError(labs.error) && labs.error.code === "PERMISSION_DENIED";
  return (
    <section className="screen" id="s-lens-options">
      <div className="content">
        <PageHead title="Lens options" sub="A lens lab's coatings, tints and PPF, chosen with its lenses on an order from Sprint 5 · priced with the lens"
          actions={<><Link className="btn" to="/products">Products</Link>{canWrite && <button className="btn primary" type="button" disabled={!online || !lab} title={title ?? (!lab ? "Choose a lens lab first" : undefined)} onClick={() => setEditing("new")}><Icon name="plus" />Add option</button>}</>} />
        {notice && <Alert tone="ok" actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}><b>{notice}</b></Alert>}
        {error !== null && (isApiError(error.err) && error.err.code === "VERSION_CONFLICT"
          ? <Alert tone="warn" role="alert"><b>{error.label} was changed since the list was read; it has been read again.</b> Check it, then try again.</Alert>
          : <Alert tone="crit" role="alert"><b>Not changed.</b> {refusalWords(error.err)}</Alert>)}
        {denied ? <NoAccessPanel page="Lens options" error={labs.error} /> : (
          <div className="toolbar">
            <Select aria-label="Lens lab" boxClass="w-sel" value={supplierId} onChange={(e) => setSp((p) => { const n = new URLSearchParams(p); if (e.target.value) n.set("supplier", e.target.value); else n.delete("supplier"); return n; }, { replace: true })}>
              <option value="">Lens lab · choose</option>
              {labs.data?.data.items.map((l) => <option key={l.id} value={l.id}>Lens lab · {l.name}</option>)}
            </Select>
            <Checkbox checked={showInactive} onChange={(v) => setSp((p) => { const n = new URLSearchParams(p); if (v) n.set("inactive", "true"); else n.delete("inactive"); return n; }, { replace: true })}>Show inactive</Checkbox>
            <span className="right small faint">Only active lens labs are listed</span>
          </div>
        )}
        {labs.isPending && <Loading what="the lens labs" />}
        {supplierId === "" && labs.data && <Panel><Empty title="Choose a lens lab.">Its coatings, tints and PPF are listed by kind.</Empty></Panel>}
        {supplierId !== "" && list.isPending && <Loading what="the options" />}
        {list.isError && <ErrorAlert error={list.error} />}
        {list.data && LENS_COMPONENT_OPTION_KINDS.map((kind) => {
          const ofKind = rows.filter((o) => o.kind === kind);
          return (
            <Panel key={kind} title={LENS_COMPONENT_OPTION_KIND_LABELS[kind]} meta={`${ofKind.length} option${ofKind.length === 1 ? "" : "s"}${lab ? ` · ${lab.name}` : ""}`} tight>
              {ofKind.length === 0 ? <Empty title={`No ${kindWord(kind)} options.`} /> : (
                <div className="tbl-scroll"><table>
                  <thead><tr><th>Code</th><th>Description</th><th>Tariff</th><th className="r">Cost ex VAT</th><th className="r">Price incl. VAT</th><th>For lens</th>{kind === "Colour" && <th>Tint</th>}<th>Status</th>{canWrite && <th />}</tr></thead>
                  <tbody>
                    {ofKind.map((o) => (
                      <tr key={o.id}>
                        <td className="mono">{o.supplierCode}</td>
                        <td>{o.description}</td>
                        <td className="mono">{o.tariffCode ?? <span className="faint">—</span>}</td>
                        <td className="money">{o.costCents === null ? <span className="faint">—</span> : formatMoney(o.costCents)}</td>
                        <td className="money strong">{formatMoney(o.sellingPriceCents)}</td>
                        <td>{o.appliesTo ? <><span className="mono">{o.appliesTo.sku}</span> {o.appliesTo.name}</> : <span className="faint">every {lab?.name ?? "lab"} lens</span>}</td>
                        {kind === "Colour" && <td className="nw">{o.saturation !== null ? `${o.saturation}%` : "—"}{o.gradient ? " · gradient" : ""}</td>}
                        <td>{o.isActive ? <Chip tone="ok" dot>Active</Chip> : <Chip tone="neutral">Inactive</Chip>}</td>
                        {canWrite && (
                          <td className="r nw">
                            <button className="btn sm" type="button" disabled={!online} title={title} onClick={() => setEditing(o)}>Edit</button>{" "}
                            <button className="btn sm" type="button" disabled={!online || toggling !== null} title={title} onClick={() => void toggle(o)}>{toggling === o.id ? (o.isActive ? "Deactivating…" : "Reactivating…") : o.isActive ? "Deactivate" : "Reactivate"}</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </Panel>
          );
        })}
        {editing !== null && lab && (
          <OptionModal supplier={lab} current={editing === "new" ? null : editing} onClose={() => setEditing(null)} onConflict={refresh}
            onSaved={(o, created) => { setEditing(null); setNotice(`${o.kindLabel} ${o.supplierCode} ${o.description} ${created ? "added" : "saved"}.`); refresh(); }}
            onCarriedOut={(words) => { setEditing(null); setNotice(words); refresh(); }} />
        )}
      </div>
    </section>
  );
}

const OPTION_FIELDS = new Set(["kind", "supplierCode", "description", "tariffCode", "costCents", "sellingPriceCents", "appliesToProductId", "saturation", "gradient", "isActive"]);

function OptionModal({ supplier, current, onClose, onSaved, onCarriedOut, onConflict }: { supplier: { id: string; name: string }; current: LensComponentOptionView | null; onClose: () => void; onSaved: (o: LensComponentOptionView, created: boolean) => void; onCarriedOut: (words: string) => void; onConflict: () => void }) {
  const [kind, setKind] = useState<LensComponentOptionKind>(current?.kind ?? "Coating");
  const [code, setCode] = useState(current?.supplierCode ?? "");
  const [description, setDescription] = useState(current?.description ?? "");
  const [tariff, setTariff] = useState(current?.tariffCode ?? "");
  const [cost, setCost] = useState(textFromCents(current?.costCents));
  const [price, setPrice] = useState(textFromCents(current?.sellingPriceCents));
  const [lens, setLens] = useState<ProductSummary | null>(current?.appliesTo ? ({ id: current.appliesTo.productId, sku: current.appliesTo.sku, displayName: current.appliesTo.name } as ProductSummary) : null);
  const [saturation, setSaturation] = useState(current?.saturation !== null && current?.saturation !== undefined ? String(current.saturation) : "");
  const [gradient, setGradient] = useState(current?.gradient ?? false);
  const [active, setActive] = useState(current?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});
  const idem = useMemo(() => newIdempotencyKey(), []);
  const tint = kind === "Colour";
  const s = useSessionView();
  const branch = activeBranchName(s) ?? "this branch";
  // Suggest's answer (UC-CATALOGUE-009 step 3, A7): the line under the price saying what it came from, or that no rule applies.
  const [suggested, setSuggested] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const suggest = async () => {
    const branchId = s?.activeBranchId ?? null;
    if (suggesting || !branchId) return;
    const costCents = centsFromText(cost);
    if (costCents === null) { setLocal((x) => ({ ...x, costCents: cost.trim() ? "Rand and cents of at least R 0.00" : "Enter the cost to suggest a price" })); return; }
    setLocal((x) => { const { costCents: _, sellingPriceCents: __, ...rest } = x; return rest; });
    setSuggesting(true);
    setSuggested(null);
    try {
      const { data } = await pricing.suggest({ branchId, supplierId: supplier.id, netCostCents: costCents });
      if (data.proposal) {
        setPrice(textFromCents(data.proposal.suggestedIncVatCents));
        const list = data.proposal.priceList?.name;
        setSuggested(`${supplier.name}'s lens rule at ${branch}: +${percentFromBp(data.proposal.percentBp)} on the cost${list ? ` · ${list}` : ""}`);
      } else {
        setSuggested(`No lens rule applies to ${supplier.name} at ${branch}: no price list of theirs is in force there. Type the selling price.`);
      }
    } catch (err) { setSuggested(`Not suggested: ${describeError(err)}`); }
    finally { setSuggesting(false); }
  };
  const fieldErr = (path: string): string | undefined => {
    if (local[path]) return local[path];
    const f = isApiError(error) ? error.field(path) : undefined;
    if (!f) return undefined;
    if (path === "supplierCode" && f.code === "not_unique") return `${supplier.name} already has a ${kindWord(kind)} with this code`;
    if ((path === "costCents" || path === "sellingPriceCents") && f.code !== "required") return "Rand and cents of at least R 0.00";
    if (path === "sellingPriceCents" && f.code === "required") return "Enter the selling price";
    if (path === "appliesToProductId") return f.code === "other_supplier" ? `A lens of ${supplier.name}` : f.code === "not_a_lens" ? "A lens only" : f.code === "inactive" ? "That lens is inactive" : f.message;
    return f.message;
  };
  const unplaced = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors.filter((f) => !OPTION_FIELDS.has(f.path)) : [];
  const conflict = isApiError(error) && error.code === "VERSION_CONFLICT";
  // Every refusal no field holds is said above the fields, in words (D2-3): a 500, a 403, a 429, a reused key, a 404.
  const general = error !== null && !conflict && !(isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.length > 0 && unplaced.length === 0);
  const submit = async () => {
    if (busy) return;
    const issues: Record<string, string> = {};
    if (!code.trim()) issues.supplierCode = "Enter the supplier's code";
    if (!description.trim()) issues.description = "Enter the description";
    const priceCents = centsFromText(price);
    if (priceCents === null) issues.sellingPriceCents = price.trim() ? "Rand and cents of at least R 0.00" : "Enter the selling price";
    const costCents = cost.trim() ? centsFromText(cost) : null;
    if (cost.trim() && costCents === null) issues.costCents = "Rand and cents of at least R 0.00";
    if (tint && saturation.trim() && !/^\d+$/.test(saturation.trim())) issues.saturation = "0 to 100 whole per cent";
    setLocal(issues);
    if (Object.keys(issues).length > 0) return;
    const sat = tint && saturation.trim() ? Number(saturation.trim()) : null;
    setBusy(true);
    setError(null);
    try {
      if (current) {
        const body: OptionBody = {};
        if (code.trim() !== current.supplierCode) body.supplierCode = code.trim();
        if (description.trim() !== current.description) body.description = description.trim();
        if ((tariff.trim() || null) !== current.tariffCode) body.tariffCode = tariff.trim() || null;
        if (costCents !== current.costCents) body.costCents = costCents;
        if (priceCents !== current.sellingPriceCents) body.sellingPriceCents = priceCents;
        if ((lens?.id ?? null) !== (current.appliesTo?.productId ?? null)) body.appliesToProductId = lens?.id ?? null;
        if (tint && sat !== current.saturation) body.saturation = sat;
        if (tint && gradient !== current.gradient) body.gradient = gradient;
        if (active !== current.isActive) body.isActive = active;
        if (Object.keys(body).length === 0) { onClose(); return; }
        const { data } = await lensOptions.patch(current.id, body, etagOf(current));
        onSaved(data, false);
      } else {
        const body: OptionBody = { supplierId: supplier.id, kind, supplierCode: code.trim(), description: description.trim(), sellingPriceCents: priceCents };
        if (tariff.trim()) body.tariffCode = tariff.trim();
        if (costCents !== null) body.costCents = costCents;
        if (lens) body.appliesToProductId = lens.id;
        if (tint && sat !== null) body.saturation = sat;
        if (tint && gradient) body.gradient = true;
        const { data } = await lensOptions.create(body, idem);
        onSaved(data, true);
      }
    } catch (err) {
      // Add pressed again after a lost answer, under the dialog's one key, answered as carried out — added once, its answer not kept:
      // the options, read again, list it (UC-CATALOGUE-009 A9; BR-GOVERNANCE-016).
      if (!current && carriedOut(err)) { onCarriedOut(`${LENS_COMPONENT_OPTION_KIND_LABELS[kind]} ${code.trim()} ${description.trim()} added: its answer did not arrive, so it is listed here.`); return; }
      setError(err);
      if (isApiError(err) && err.code === "VERSION_CONFLICT") onConflict();
    }
    finally { setBusy(false); }
  };
  return (
    <Modal title={current ? `Edit ${kindWord(current.kind)} ${current.supplierCode}` : "Add an option"} meta={supplier.name} wide onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : current ? "Save changes" : "Add option"}</button>
    </>}>
      {conflict && <Alert tone="warn" role="alert"><b>Someone changed this option since the list was read.</b> The list has been read again — close this and open the option again to see the change.</Alert>}
      {general && <Alert tone="crit" role="alert"><b>Not saved.</b> {unplaced.length > 0 ? unplaced.map((f) => f.message).join(" · ") : refusalWords(error)}</Alert>}
      <div className="grid-2">
        <Field label="Kind" required htmlFor="opt-kind" help={current ? "Fixed once the option exists" : undefined}>
          <Select id="opt-kind" value={kind} disabled={current !== null} onChange={(e) => setKind(e.target.value as LensComponentOptionKind)}>
            {LENS_COMPONENT_OPTION_KINDS.map((k) => <option key={k} value={k}>{LENS_COMPONENT_OPTION_KIND_LABELS[k]}</option>)}
          </Select>
        </Field>
        <Field label="Supplier's code" required htmlFor="opt-code" error={fieldErr("supplierCode")}><TextInput id="opt-code" mono maxLength={CATALOGUE_LIMITS.optionCodeMax} value={code} onChange={(e) => setCode(e.target.value)} error={!!fieldErr("supplierCode")} /></Field>
        <Field label="Description" required htmlFor="opt-desc" error={fieldErr("description")}><TextInput id="opt-desc" maxLength={CATALOGUE_LIMITS.optionDescriptionMax} value={description} onChange={(e) => setDescription(e.target.value)} error={!!fieldErr("description")} /></Field>
        <SuggestInput<SaoaCodeView> id="opt-tariff" label="Tariff code" mono text={tariff} onText={setTariff} onPick={(t) => setTariff(t.code)} error={fieldErr("tariffCode")} keyOf={(t) => t.code}
          renderOption={(t) => <span className="primary"><span className="mono">{t.code}</span> {t.description}</span>}
          source={{ kind: "remote", key: ["reference", "saoa"], search: async (q) => (await tariffCodes.search(q)).data.items, minChars: 2 }} />
        <Field label="Cost ex VAT" htmlFor="opt-cost" error={fieldErr("costCents")} help="What the lab charges, optional"><TextInput id="opt-cost" mono inputMode="decimal" placeholder="e.g. 180.00" value={cost} onChange={(e) => setCost(e.target.value)} error={!!fieldErr("costCents")} /></Field>
        <Field label="Selling price incl. VAT" required htmlFor="opt-price" error={fieldErr("sellingPriceCents")} help={suggested ? <span role="status">{suggested}</span> : "Added to the lens's price when chosen with it"}>
          <div className="row c">
            <TextInput id="opt-price" mono inputMode="decimal" placeholder="e.g. 340.00" value={price} onChange={(e) => { setPrice(e.target.value); setSuggested(null); }} error={!!fieldErr("sellingPriceCents")} boxClass="grow" />
            <button className="btn sm" type="button" disabled={suggesting} onClick={() => void suggest()} title={`The price ${supplier.name}'s lens rule gives for the cost at ${branch}`}>{suggesting ? "Suggesting…" : "Suggest"}</button>
          </div>
        </Field>
        {tint && <Field label="Saturation %" htmlFor="opt-sat" error={fieldErr("saturation")}><TextInput id="opt-sat" mono inputMode="numeric" value={saturation} onChange={(e) => setSaturation(e.target.value)} error={!!fieldErr("saturation")} /></Field>}
        {tint && <div className="field"><label>Gradient</label><Checkbox checked={gradient} onChange={setGradient}>A gradient tint</Checkbox></div>}
      </div>
      <ProductPicker id="opt-lens" label="Restricted to lens" value={lens} onPick={setLens} categories={["PlasticLens", "GlassLens"]} supplierId={supplier.id} keyTag={`option-${supplier.id}`}
        allow={(p) => p.supplier?.id === supplier.id} error={fieldErr("appliesToProductId")} help={`Left empty, every ${supplier.name} lens offers it`} />
      {current && <Checkbox checked={active} onChange={setActive}>Active — offered with {supplier.name}'s lenses</Checkbox>}
    </Modal>
  );
}
